/*
 * App-wide "time changed" bus. The assistant's time tools report
 * `invalidate: ["time"]` from any chat (Case IDE, the agent panel, Home), and
 * an undo of a time change lands here too; every open timesheet, tracker
 * chip and case pane refetches.
 */
import { useEffect, useRef } from "react";

type Listener = () => void;

const listeners = new Set<Listener>();

export function notifyTimeChanged(): void {
  for (const fn of Array.from(listeners)) fn();
}

/** Forward a `data-invalidate` list; only `time` matters here. */
export function notifyTimeInvalidated(targets: readonly string[]): void {
  if (targets.includes("time")) notifyTimeChanged();
}

export function subscribeTimeChanged(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Run `onChange` whenever time changed elsewhere (latest callback wins). */
export function useOnTimeChanged(onChange: Listener): void {
  const ref = useRef(onChange);
  ref.current = onChange;
  useEffect(() => subscribeTimeChanged(() => ref.current()), []);
}
